import { cn } from "@/common/helpers/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/user-avatar/user-avatar";
import type React from "react";
import { Link } from "wouter";

export interface UserItemUser {
  uuid: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  azureObjectUuid?: string | null;
}

export enum UserItemVariant {
  Default = "Default",
  Compact = "Compact",
}

export interface UserItemProps {
  user: UserItemUser;
  to?: string;
  showEmail?: boolean;
  highlighted?: boolean;
  badge?: React.ReactNode;
  endContent?: React.ReactNode;
  tooltip?: string;
  variant?: UserItemVariant;
  className?: string;
}

export function UserItem({
  user,
  to,
  showEmail,
  highlighted,
  badge,
  endContent,
  tooltip,
  variant = UserItemVariant.Default,
  className,
}: UserItemProps) {
  const displayName = [user.lastName, user.firstName].filter(Boolean).join(", ");
  const isCompact = variant === UserItemVariant.Compact;

  const content = isCompact ? (
    <>
      <div className="[&>span]:h-6 [&>span]:w-6 [&>span]:text-[10px]">
        <UserAvatar userAzureObjectUuid={user.azureObjectUuid} name={displayName} />
      </div>
      <span className="text-sm">{displayName}</span>
      {endContent && <span className="flex shrink-0 items-center gap-1">{endContent}</span>}
    </>
  ) : (
    <>
      <UserAvatar userAzureObjectUuid={user.azureObjectUuid} name={displayName} />

      <div className="flex flex-1 flex-col overflow-hidden">
        <span className="truncate text-sm font-semibold">{displayName}</span>
        {showEmail && user.email && <span className="text-muted-foreground truncate text-xs">{user.email}</span>}
      </div>

      {badge && <div className="shrink-0">{badge}</div>}

      {endContent && <div className="flex shrink-0 gap-1">{endContent}</div>}
    </>
  );

  const containerClasses = isCompact
    ? cn("flex items-center gap-2 transition-colors", to && "hover:text-primary cursor-pointer", className)
    : cn(
        "bg-card flex items-center gap-3 rounded-lg border p-3 transition-colors",
        to && "hover:bg-muted/30 hover:border-primary/60 cursor-pointer",
        highlighted ? "border-primary/20 bg-primary/10 dark:bg-primary/5" : "border-border",
        className,
      );

  const item = to ? (
    <Link to={to} className={containerClasses}>
      {content}
    </Link>
  ) : (
    <div className={containerClasses}>{content}</div>
  );

  if (!tooltip) {
    return item;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{item}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
